import { useState } from "react";
import { toast } from "sonner";
import { CalendarDays, CheckCircle2, CalendarPlus, Phone } from "lucide-react";
import { api, API } from "@/lib/api";
import { track, trackPhoneClick } from "@/lib/analytics";
import { SITE, PRIMARY_PHONE } from "@/lib/site";
import LegalNotice from "@/components/LegalNotice";
import FormConsent from "@/components/FormConsent";

const TIMES = ["9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM"];

function todayISO() {
  return new Date().toISOString().split("T")[0];
}

export default function Consultation() {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "",
    preferred_date: "", preferred_time: "", topic: "", notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [termsOk, setTermsOk] = useState(false);
  const [booking, setBooking] = useState(null);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/consultations", form);
      toast.success("Consultation booked!");
      setBooking({ id: data.id, date: form.preferred_date, time: form.preferred_time });
      track("consultation_booked", { date: form.preferred_date, time: form.preferred_time });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Booking failed");
    } finally {
      setLoading(false);
    }
  };

  if (booking) {
    const icsUrl = `${API}/consultations/${booking.id}/calendar.ics`;
    return (
      <section className="section" data-testid="consult-success">
        <div className="container-x max-w-2xl soft-card text-center">
          <CheckCircle2 className="w-14 h-14 text-teal-600 mx-auto" />
          <h1 className="mt-6 text-3xl font-semibold text-slate-900">Consultation booked.</h1>
          <p className="mt-4 text-slate-600">
            We'll confirm your appointment shortly. Add it to your calendar so it's easy to find —
            your download works with Google Calendar, Apple Calendar and Outlook.
          </p>
          <div className="mt-4 text-sm text-slate-500">
            {booking.date} at {booking.time}
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a href={icsUrl} className="btn-primary" data-testid="consult-download-ics">
              <CalendarPlus className="w-4 h-4" /> Add to calendar (.ics)
            </a>
            <a
              href={`tel:${PRIMARY_PHONE.tel}`}
              onClick={trackPhoneClick("consultation-success")}
              className="btn-outline"
              data-testid="consult-success-call"
            >
              <Phone className="w-4 h-4" /> Call {PRIMARY_PHONE.display}
            </a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <div data-testid="consultation-page">
      <section className="section bg-gradient-to-b from-slate-50 to-white">
        <div className="container-x max-w-3xl">
          <div className="eyebrow">Free consultation</div>
          <h1 className="mt-4 text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.05]">
            Schedule a <span className="brand-gradient-text">free 20-minute</span> care consultation.
          </h1>
          <p className="mt-5 text-slate-600 leading-relaxed">
            Talk to a real care coordinator — no pressure, no obligation. We'll listen, answer questions and share a plan that fits.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="container-x max-w-3xl">
          <div className="mb-5 grid gap-3">
            <LegalNotice variant="emergency" testId="cn-emergency-notice">
              For any medical emergency, <strong>call 911</strong>. Consultation bookings are not monitored 24/7 for urgent care.
            </LegalNotice>
            <LegalNotice variant="noshow" testId="cn-noshow-notice">
              Please give us at least 24 hours notice if you need to reschedule or cancel. Repeated no-shows may
              incur a fee or suspend future scheduling privileges (Terms §13).
            </LegalNotice>
          </div>
          <form onSubmit={submit} className="soft-card space-y-6" data-testid="consultation-form">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="label-field">Full name *</label>
                <input required value={form.full_name} onChange={upd("full_name")} className="input-field" data-testid="cn-name" />
              </div>
              <div>
                <label className="label-field">Phone *</label>
                <input required type="tel" value={form.phone} onChange={upd("phone")} className="input-field" data-testid="cn-phone" />
              </div>
              <div className="md:col-span-2">
                <label className="label-field">Email *</label>
                <input required type="email" value={form.email} onChange={upd("email")} className="input-field" data-testid="cn-email" />
              </div>
              <div>
                <label className="label-field">Preferred date *</label>
                <input required type="date" min={todayISO()} value={form.preferred_date} onChange={upd("preferred_date")} className="input-field" data-testid="cn-date" />
              </div>
              <div>
                <label className="label-field">Preferred time *</label>
                <select required value={form.preferred_time} onChange={upd("preferred_time")} className="input-field" data-testid="cn-time">
                  <option value="">Select time</option>
                  {TIMES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="label-field">What would you like to discuss?</label>
                <input value={form.topic} onChange={upd("topic")} className="input-field" data-testid="cn-topic" placeholder="e.g. Dad needs help after hospital discharge" />
              </div>
              <div className="md:col-span-2">
                <label className="label-field">Notes</label>
                <textarea rows={4} value={form.notes} onChange={upd("notes")} className="input-field" data-testid="cn-notes" />
              </div>
            </div>

            <FormConsent type="consultation" checked={termsOk} onChange={(e) => setTermsOk(e.target.checked)} testId="cn-consent" />

            <button disabled={loading} className="btn-primary" data-testid="cn-submit">
              <CalendarDays className="w-4 h-4" /> {loading ? "Booking…" : "Book consultation"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
